/**
 * project (프로젝트) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { DefaultBasicInfoSchema } from "./default-schemes";
import { validateArrayWithSchema, validateWithSchema, } from "@erp/shared/utils/https/zod-validation";
import z from "zod";
// ── 스키마 & 타입 (Project) ──
export const GetProjectResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    projectCode: z.string(),
    name: z.string().nullable(),
    isPreOrder: z.number().int().min(0).max(1),
    status: z.enum(["진행중", "중단", "완료"]),
    managerId: z.number().nullable(),
    companyName: z.string().nullable(),
});
// ── 스키마 & 타입 (Process) ──
const ProcessConfirmationResponseSchema = z.object({
    id: z.coerce.number(),
    approvalPhase: z.string().nullable(),
    createrName: z.string().nullable(),
    createdTime: z.string(),
    isHistory: z.number(),
    originId: z.coerce.number(),
    version: z.number().optional(),
    checkerName: z.string().nullable().optional(),
    approverName: z.string().nullable().optional(),
    companyName: z.string().nullable().optional(),
    inhouseBidNumber: z.string().nullable().optional(),
    companyBidNumber: z.string().nullable().optional(),
    totalPrice: z.coerce.number().nullable().optional(),
});
const ProcessItemResponseSchema = z.object({
    isCompleted: z.boolean(),
    items: z.array(ProcessConfirmationResponseSchema),
});
export const GetProcessResponseSchema = z.object({
    id: z.coerce.number(),
    projectCode: z.string(),
    estimate: ProcessItemResponseSchema,
    obtain: ProcessItemResponseSchema,
    purchase: ProcessItemResponseSchema,
    order: ProcessItemResponseSchema,
    inbound: ProcessItemResponseSchema,
    outbound: ProcessItemResponseSchema,
});
// ── 스키마 & 타입 (ActivityLog) ──
const ActivityLogItemSchema = z.object({
    id: z.coerce.number(),
    documentType: z.string(),
    eventType: z.string(),
    documentId: z.number().nullable(),
    actorId: z.number(),
    actorName: z.string(),
    summary: z.string(),
    metadata: z.record(z.unknown()).nullable(),
    createdTime: z.string(),
});
const GetActivityLogResponseSchema = z.object({
    total: z.number(),
    items: z.array(ActivityLogItemSchema),
});
// ── 스키마 & 타입 (Comment) ──
export const CommentUserSchema = z
    .object({
    id: z.number(),
    userName: z.string(),
    givenName: z.string(),
    profileImageUrl: z.string().nullable(),
})
    .strict();
export const CommentSchema = z.lazy(() => z
    .object({
    id: z.coerce.number(),
    parentId: z.number().nullable(),
    projectId: z.number(),
    content: z.string(),
    createdTime: z.string(),
    modifiedTime: z.string(),
    deletedYn: z.number(),
    creater: CommentUserSchema,
    children: z.array(CommentSchema),
})
    .strict());
// ── API 함수 (Project CRUD) ──
export async function getProject(projectCode) {
    const data = await customInstance({
        url: "/project",
        method: "GET",
        params: { projectCode },
    });
    return validateArrayWithSchema(GetProjectResponseSchema, data);
}
export const postProject = async (projectCode, isPreOrder, name) => {
    await customInstance({
        url: "/project",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: { projectCode, isPreOrder, name },
    });
};
export const patchProject = async (projectId, body) => {
    await customInstance({
        url: `/project/${projectId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export const deleteProject = async (projectId) => {
    await customInstance({
        url: `/project/${projectId}`,
        method: "DELETE",
    });
};
export const patchProjectStatus = async (projectId, status) => {
    return customInstance({
        url: `/project/${projectId}/status`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: { status },
    });
};
export async function checkProjectDuplicate(projectCode, excludeId) {
    const params = { projectCode };
    if (excludeId != null) {
        params.excludeId = excludeId;
    }
    const response = await customInstance({
        url: "/project/check-duplicate",
        method: "GET",
        params,
    });
    return response.exists;
}
// ── API 함수 (Trash) ──
export async function getProjectTrash() {
    return customInstance({
        url: "/project/trash",
        method: "GET",
    });
}
export async function permanentDeleteProject(projectId) {
    await customInstance({
        url: `/project/${projectId}/permanent`,
        method: "DELETE",
    });
}
export async function restoreProject(projectId) {
    await customInstance({
        url: `/project/${projectId}/restore`,
        method: "PATCH",
    });
}
// ── API 함수 (Process & ActivityLog) ──
export async function getProcess(projectId) {
    const data = await customInstance({
        url: `/project/${projectId}/process`,
        method: "GET",
    });
    return validateWithSchema(GetProcessResponseSchema, data);
}
export async function getActivityLog(projectId, params) {
    const data = await customInstance({
        url: `/project/${projectId}/activity`,
        method: "GET",
        params,
    });
    return validateWithSchema(GetActivityLogResponseSchema, data);
}
// ── API 함수 (Comment) ──
export async function getComment(projectId) {
    const data = await customInstance({
        url: `/project/${projectId}/comment`,
        method: "GET",
    });
    return validateArrayWithSchema(CommentSchema, data);
}
export const postComment = async (body) => {
    return customInstance({
        url: "/project-comment",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export const patchComment = async (commentId, body) => {
    return customInstance({
        url: `/project-comment/${commentId}`,
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        data: body,
    });
};
export const deleteComment = async (commentId) => {
    return customInstance({
        url: `/project-comment/${commentId}`,
        method: "DELETE",
    });
};
// ── SDK 객체 ──
export const projectApi = {
    getProject,
    postProject,
    patchProject,
    deleteProject,
    patchProjectStatus,
    checkProjectDuplicate,
    getProjectTrash,
    permanentDeleteProject,
    restoreProject,
    getProcess,
    getActivityLog,
    getComment,
    postComment,
    patchComment,
    deleteComment,
};
